/**
 * Erasing every answer of a survey, and the four things somebody has to know before doing it.
 *
 * This is the one irreversible action in the panel that reaches everybody at once, so the dialog is
 * not a confirmation with a scary colour. It states, in this order:
 *
 * 1. **how many rows go** — the survey's own `responseCount`, every version and every status, which
 *    is exactly what `POST /:id/purge` deletes. Read from the survey, never estimated from the
 *    version on screen: the cards above cover one version, and the purge does not;
 * 2. **what goes with them** — answers, selected options and step timings, by cascade;
 * 3. **what stays** — the survey, the questionnaire, the translations, the version and the approval
 *    state. Without that line nobody can tell whether they are erasing the answers or the survey;
 * 4. **the consequence** — everyone who answered becomes eligible again, so the survey goes back to
 *    those people.
 *
 * And it is unlocked by typing the slug, **exactly**: no trim, no case folding. The BFF compares
 * `confirmSlug` with `===` and refuses with `400` otherwise, so a field that was kinder than the
 * server would only move the refusal one step later. Pasting the title, or the slug with a trailing
 * space, keeps the button dead — which is the point of asking.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTModal, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import { formatCount } from '../../../Utils/surveyFormat';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
`;

const dangerStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const headlineStyle = css`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const lineStyle = css`
  margin: 0;
`;

const keepsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const labelStyle = css`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

export interface SurveyPurgeDialogProps {
  slug: string;
  /** Responses across every version and every status — what the purge deletes, to the row. */
  responseCount: number;
  isWorking: boolean;
  onPurge: (confirmSlug: string) => void;
  onClose: () => void;
}

export const SurveyPurgeDialog = ({
  slug,
  responseCount,
  isWorking,
  onPurge,
  onClose,
}: SurveyPurgeDialogProps): JSX.Element => {
  const [typed, setTyped] = useState('');

  /** Exact, deliberately: the BFF compares with `===`, and so does the button. */
  const unlocked = typed === slug;

  const isOne = responseCount === 1;
  const countLabel = isOne ? '1 resposta' : `${formatCount(responseCount)} respostas`;

  /*
    Nothing to erase is its own state, not a confirmation for zero rows. The count is refetched on
    the way in, so this is the survey as it stands, and offering "apagar 0 respostas" behind a slug
    field would be ceremony around a no-op.
  */
  if (responseCount === 0) {
    return (
      <UTModal
        open
        position='center'
        maxWidth='480px'
        hasButtonClose
        modalContentId='survey-purge-dialog'
        closeButtonId='survey-purge-dialog-close'
        title='Apagar todas as respostas'
        content={
          <div className={contentStyle}>
            <div className={keepsStyle}>
              <p className={headlineStyle}>Não há o que apagar</p>
              <p className={lineStyle}>
                Esta pesquisa não tem nenhuma resposta guardada — nem nesta versão, nem em versão
                nenhuma.
              </p>
            </div>
          </div>
        }
        customButtons={[{ text: 'Fechar', variant: 'secondary', onClick: onClose }]}
        onClose={onClose}
      />
    );
  }

  return (
    <UTModal
      open
      position='center'
      maxWidth='560px'
      hasButtonClose
      modalContentId='survey-purge-dialog'
      closeButtonId='survey-purge-dialog-close'
      title='Apagar todas as respostas'
      content={
        <div className={contentStyle}>
          <div className={dangerStyle} role='alert'>
            <p className={headlineStyle}>
              {isOne ? 'Será apagada' : 'Serão apagadas'} {countLabel} de {slug} — de todas as
              pessoas e de todas as versões. Não há como desfazer.
            </p>
            <p className={lineStyle}>
              Vão junto, em cascata: o que cada pessoa respondeu, as opções que marcou e os tempos
              por passo.
            </p>
            <p className={lineStyle}>
              Quem já tinha respondido <strong>volta a ficar elegível</strong>: a pesquisa aparece
              de novo para essas pessoas.
            </p>
          </div>

          <div className={keepsStyle}>
            <p className={headlineStyle}>O que não é apagado</p>
            <p className={lineStyle}>
              A pesquisa, o questionário, as traduções, a versão e o estado de aprovação continuam
              exatamente como estão. Some o que as pessoas responderam, não a pesquisa.
            </p>
          </div>

          <div className={fieldStyle}>
            <span className={labelStyle}>
              Para liberar, digite <span className={slugStyle}>{slug}</span>
            </span>
            <UTTextField
              id='survey-purge-slug'
              placeholder={slug}
              autoComplete='off'
              value={typed}
              onChange={event => {
                setTyped(event.target.value);
              }}
            />
            <p className={hintStyle}>
              Exato, sem espaço sobrando e respeitando maiúsculas e minúsculas — é o mesmo texto que
              o BFF confere antes de apagar qualquer coisa.
            </p>
          </div>
        </div>
      }
      customButtons={[
        { text: 'Cancelar', variant: 'secondary', disabled: isWorking, onClick: onClose },
        {
          text: `Apagar ${countLabel}`,
          state: 'danger',
          loading: isWorking,
          disabled: !unlocked || isWorking,
          onClick: () => {
            onPurge(typed);
          },
        },
      ]}
      onClose={onClose}
    />
  );
};
